/**
 * Fetches the admin-configured tracking-pixel IDs on app boot and injects
 * the standard <script> snippets for whichever ones are enabled. Each
 * pixel is independent — only the ones with a non-empty ID get loaded.
 *
 * Supported pixels:
 *   • Facebook Pixel (fbq)
 *   • Google Analytics 4   (G-XXXXXXXXXX)
 *   • Google Ads conversion (AW-XXXXXXXXX)
 *   • Google Tag Manager   (GTM-XXXXXXX)  — works alongside the others
 */
import { useEffect } from "react";
import axios from "axios";
import { API } from "@/api";

const PIXEL_MARK = "data-vbs-pixel";

function injectScriptSrc(src, id, extraAttrs = {}) {
  if (document.querySelector(`script[${PIXEL_MARK}="${id}"]`)) return;
  const s = document.createElement("script");
  s.async = true;
  s.src = src;
  s.setAttribute(PIXEL_MARK, id);
  Object.entries(extraAttrs).forEach(([k, v]) => s.setAttribute(k, v));
  document.head.appendChild(s);
}

function injectInline(code, id) {
  if (document.querySelector(`script[${PIXEL_MARK}="${id}"]`)) return;
  const s = document.createElement("script");
  s.textContent = code;
  s.setAttribute(PIXEL_MARK, id);
  document.head.appendChild(s);
}

function installFacebook(pid) {
  if (!pid) return;
  injectInline(`
    !function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?
    n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;
    n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;
    t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,
    document,'script','https://connect.facebook.net/en_US/fbq.js');
    fbq('init', '${pid}'); fbq('track', 'PageView');
  `, `fb-${pid}`);
}

function installGtag(measurementId) {
  if (!measurementId) return;
  injectScriptSrc(`https://www.googletagmanager.com/gtag/js?id=${measurementId}`, `gtag-${measurementId}`);
  injectInline(`
    window.dataLayer = window.dataLayer || [];
    function gtag(){dataLayer.push(arguments);}
    window.gtag = window.gtag || gtag;
    gtag('js', new Date());
    gtag('config', '${measurementId}');
  `, `gtag-init-${measurementId}`);
}

function installGTM(gtmId) {
  if (!gtmId) return;
  injectInline(`
    (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
    new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
    j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
    'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
    })(window,document,'script','dataLayer','${gtmId}');
  `, `gtm-${gtmId}`);
  // <noscript> iframe for GTM
  if (!document.querySelector(`noscript[${PIXEL_MARK}="gtm-ns-${gtmId}"]`)) {
    const ns = document.createElement("noscript");
    ns.setAttribute(PIXEL_MARK, `gtm-ns-${gtmId}`);
    ns.innerHTML = `<iframe src="https://www.googletagmanager.com/ns.html?id=${gtmId}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`;
    document.body.insertBefore(ns, document.body.firstChild);
  }
}

export default function usePixels() {
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/settings/pixels`);
        if (cancelled) return;
        installFacebook(data.fb_pixel_id);
        installGtag(data.ga4_id);
        installGtag(data.google_ads_id);
        installGTM(data.gtm_id);
      } catch (_) { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, []);
}
